import { Check, Zap } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { IconBadge } from "@/components/waggle/PageShell";
import { cn } from "@/lib/utils";
import { FREE_WEEKLY_LIMIT, PLANS, PRO_PRICE_MONTHLY, formatShortDate, useUpgrade, usePlan } from "@/lib/plan";
import type { OutOfActionsInfo } from "@/lib/outOfActions";

// Free vs Pro comparison. Upgrade starts Stripe Checkout and redirects the
// browser to the Stripe-hosted page. `outOfActions` is set when the server
// refused a generation for lack of AI actions; the dialog then says so, and
// upgrading is the only way to continue before the weekly reset.
export function UpgradeDialog({
  open,
  onOpenChange,
  outOfActions,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  outOfActions?: OutOfActionsInfo | null;
}) {
  const { plan: currentPlan, billingEnabled, resetsAt } = usePlan();
  const upgrade = useUpgrade();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-0 p-0 sm:max-w-xl">
        <div className="px-6 pt-6">
          <IconBadge>
            <Zap />
          </IconBadge>
          <DialogTitle className="mt-5 pr-8 text-lg">Upgrade to Waggle Pro</DialogTitle>
          <DialogDescription className="mt-1" data-testid="upgrade-dialog-description">
            {outOfActions ? (
              <>
                {outOfActions.message} Free accounts get {outOfActions.limit ?? FREE_WEEKLY_LIMIT} AI actions a week, which
                reset {formatShortDate(outOfActions.resetsAt ?? resetsAt)}. Upgrade to Pro to keep generating now.
              </>
            ) : (
              <>
                Free accounts can create or regenerate up to {FREE_WEEKLY_LIMIT} questions a week. Pro removes the limit.
              </>
            )}
          </DialogDescription>
        </div>

        <div className="grid grid-cols-1 gap-3 px-6 pt-5 sm:grid-cols-2">
          {PLANS.map((plan) => {
            const isPro = plan.id === "pro";
            return (
              <div key={plan.id} className={cn("rounded-xl p-4", isPro ? "bg-accent ring-2 ring-brand-500" : "border")}>
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold">{plan.name}</h3>
                  {plan.id === currentPlan && (
                    <Badge variant="outline" className="bg-muted text-muted-foreground">
                      Current plan
                    </Badge>
                  )}
                </div>
                <p className="mt-2">
                  <span className="text-2xl font-semibold">{plan.price}</span>
                  <span className="ml-1 text-sm text-muted-foreground">{plan.cadence}</span>
                </p>
                <ul className="mt-3 flex flex-col gap-2">
                  {plan.features.map((f) => (
                    <li key={f} className="flex gap-2 text-sm text-muted-foreground">
                      <Check aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        <div className="flex flex-col-reverse gap-3 px-6 py-6 sm:flex-row sm:justify-end">
          <Button variant="outline" size="lg" onClick={() => onOpenChange(false)}>
            Not now
          </Button>
          <Button size="lg" disabled={!billingEnabled || upgrade.isPending} onClick={() => upgrade.mutate()} data-testid="button-upgrade-checkout">
            {upgrade.isPending ? "Redirecting to Stripe…" : `Upgrade to Pro — $${PRO_PRICE_MONTHLY}/month`}
          </Button>
        </div>
        {upgrade.isError && (
          <p role="alert" className="border-t px-6 py-3 text-xs text-destructive">
            Couldn't start checkout. Please try again.
          </p>
        )}
        {!billingEnabled && currentPlan === "pro" && (
          <p className="border-t px-6 py-3 text-xs text-muted-foreground">You're already on Pro.</p>
        )}
      </DialogContent>
    </Dialog>
  );
}
